import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { CLINIC_CHOICE, CLINIC_REQUIRED } from "@dentacore/shared-types";
import type { ClinicChoice } from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/auth-context";
import { landing } from "./landing";
import { RequiredMark } from "../ui/required-mark";

/**
 * The login screen.
 *
 * Deliberately unstyled beyond the bare minimum: the design system arrives at
 * STORY-011 and the shell at STORY-012, and building a look here would only
 * mean rebuilding it against the mockup twice. What it does carry is the markup
 * that has to be right regardless — a real `<form>` so Enter submits, labels
 * tied to inputs, and an error region screen readers announce.
 */
export function LoginPage(): React.JSX.Element {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [practice, setPractice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * **Asked for only once the server says it is needed.**
   *
   * Showing it always would put a question in front of every single-tenant
   * install and every clinic that opened its own address — the overwhelming
   * majority, for whom the answer is already known and typing it is friction
   * with no payoff. Showing it never was the bug: the reader was told to go to
   * a subdomain, with no field to name one and often no idea one existed.
   */
  const [askingPractice, setAskingPractice] = useState(false);
  /** The practices this address works at, once the server has named them (QA20-1). */
  const [choices, setChoices] = useState<ClinicChoice[] | null>(null);

  const atApex = isApex();
  // Set by the reset page on its way here, so somebody who has just changed
  // their password is told it worked rather than being dropped on a bare form
  // wondering whether it saved.
  //
  // Two ways to arrive having just set a password, because the two flows leave
  // differently: the reset link navigates within the router and carries state,
  // while changing your own password replaces the whole page — and router state
  // does not survive that.
  const passwordWasReset =
    (location.state as { passwordWasReset?: boolean } | null)?.passwordWasReset === true ||
    new URLSearchParams(location.search).get("changed") === "1";

  if (status === "signed-in") {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? "/"} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const signedIn = await signIn(email, password, practice);
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      await navigate(from ?? (await landing(signedIn)), { replace: true });
    } catch (caught) {
      /**
       * **Several practices: show them** (QA20-1).
       *
       * The server has already checked the password by the time this arrives —
       * it has to have, because the list names clinics — so this is not a
       * failure the reader has to fix. It is a question, and the answer is a
       * card. Handled before `setError`, so the chooser is not drawn under a
       * red sentence about something that did not go wrong.
       */
      if (caught instanceof ApiError && caught.code === CLINIC_CHOICE) {
        const offered = caught.details?.clinics as ClinicChoice[] | undefined;
        if (offered !== undefined && offered.length > 0) {
          setChoices(offered);
          return;
        }
      }

      // The server answers identically for an unknown address and a wrong
      // password, on purpose. Passing its message straight through keeps that
      // property instead of quietly inventing a more specific one.
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");

      // The last resort, reached now only when the chooser cannot be built —
      // every practice but one suspended. It stays revealed afterwards, because
      // a wrong practice fails as an ordinary rejected sign-in and hiding the
      // field again would take away the only thing they could correct.
      if (caught instanceof ApiError && caught.code === CLINIC_REQUIRED) setAskingPractice(true);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * **The chooser, and it replaces the form rather than sitting under it**
   * (QA20-1).
   *
   * The password has been accepted; there is nothing left to fill in, and
   * leaving the form on screen would invite somebody to retype it. The client
   * asked for *"boxes next to eachother"*, like picking a profile.
   *
   * **Anchors, not buttons.** Each practice lives at its own origin, so going
   * through one is a navigation to a different host — which `<a href>` is for.
   * A button calling `navigate()` would stay on this origin and land nowhere.
   */
  if (choices !== null) {
    return (
      /**
       * **`login-wide`, not `login`** (2026-09-20).
       *
       * The chooser inherited the sign-in column, which is `22rem` — sized for
       * one field and a button, and narrower than two cards plus their gap. So
       * `auto-fit` collapsed to a single track and the practices stacked, which
       * is the one thing the client asked this screen not to do: *"they should
       * be next to eachother tho not one below the other"*. The grid was always
       * right; it was being measured against the wrong container.
       */
      <main className="login login-wide">
        <h1>Which practice?</h1>
        <p className="quiet">
          <strong>{email}</strong> works at more than one. Pick the one you are signing in to.
        </p>

        <ul className="clinic-choices" data-testid="clinic-choices">
          {choices.map((clinic) => (
            <li key={clinic.slug}>
              <a className="clinic-choice" href={clinic.url} data-testid="clinic-choice">
                {/*
                  The initial is decoration and the name is the label, so the
                  initial is hidden from assistive technology rather than read
                  out as a stray letter before every name.
                */}
                <span className="clinic-choice-mark" aria-hidden="true">
                  {clinic.name.trim().charAt(0).toUpperCase()}
                </span>
                <span className="clinic-choice-name">{clinic.name}</span>
                <span className="clinic-choice-slug">{clinic.slug}</span>
              </a>
            </li>
          ))}
        </ul>

        <p className="quiet">
          <button type="button" className="linkish" onClick={() => setChoices(null)}>
            Use a different account
          </button>
        </p>
      </main>
    );
  }

  return (
    <main className="login">
      <h1>DentaCore</h1>

      {passwordWasReset && (
        <p role="status" data-testid="login-password-reset">
          Your password has been changed. Sign in with the new one.
        </p>
      )}

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        {askingPractice && (
          <>
            <label htmlFor="practice">
              Your practice&rsquo;s address
              <RequiredMark />
            </label>
            <input
              id="practice"
              name="practice"
              autoComplete="organization"
              autoFocus
              placeholder="cedar-dental"
              value={practice}
              onChange={(event) => setPractice(event.target.value)}
              data-testid="login-practice"
            />
            <p className="quiet">
              The first part of the web address your practice uses &mdash; paste the whole thing if
              that is easier.
            </p>
          </>
        )}

        <label htmlFor="email">
          Email
          <RequiredMark />
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="password">
          Password
          <RequiredMark />
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {error !== null && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      {/*
        **Two links that did not exist, and their absence was the whole
        problem.**

        `/forgot` is the only unauthenticated route to a password reset — the
        server has been able to send one since 2026-08-31, and nothing on this
        page offered it, so the feature was reachable only by an operator doing
        it by hand.

        `/signup` is new (2026-09-05). It is shown at the apex and hidden at a
        clinic's own subdomain, because the two audiences are different people:
        somebody at `cedar-dental.dentacore.app` already has a practice, and
        offering them "set up a practice" invites a second, empty tenant with
        their name on it — which is a support call, not a sale.
      */}
      <p className="quiet">
        <Link to="/forgot">Forgotten your password?</Link>
      </p>

      {atApex && (
        <p className="quiet">
          New to DentaCore? <Link to="/signup">Set up your practice</Link>.
        </p>
      )}
    </main>
  );
}

/**
 * Whether this browser is at the apex rather than at a clinic's own subdomain.
 *
 * Same rule the API and the API client both apply — three labels, or two when
 * the last is `localhost` — because a disagreement here would show the sign-up
 * link to a practice that already has one.
 */
function isApex(hostname: string = window.location.hostname): boolean {
  if (/^\d+(\.\d+)*$/.test(hostname)) return true;

  const labels = hostname.split(".");
  const isLoopbackName = labels.at(-1) === "localhost";
  if (labels.length < (isLoopbackName ? 2 : 3)) return true;

  const first = labels[0];
  return first === undefined || first.length === 0 || first === "www";
}
